import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { BookOpen, Bell } from 'lucide-react';
import { PageLayout } from '../../../components/ui/PageLayout';
import { StatusBadge } from '../../../components/ui/StatusBadge';
import ReflectionContent from './components/ReflectionContent';
import ReflectionProgress from './components/ReflectionProgress';
import ReflectionPromptCard from './components/ReflectionPromptCard';
import ManagerResponseCard from './components/ManagerResponseCard';
import PreviousReflections from './components/PreviousReflections';
import LoadingOverlay from './components/LoadingOverlay';
import DeadlineWarning from './components/DeadlineWarning';
import AppIcon from '../../../components/AppIcon';
import { appMonthNameYear } from '../../../utils/appDateFormat';
import { Spinner } from '../../../components/ui/Spinner';
import { Alert } from '../../../components/ui/Alert';
import Button from '../../../components/ui/Button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../components/ui/Tabs';
import { useToast, ToastRenderer } from '../../../hooks/useToast';

import * as ReflectionAPI from '../../../api/PreformanceManagement/Reflection';
import {
  MonthlyReflectionInput,
  UserReflectionView,
  ReflectionContributionStatusView,
  ManagerReflectionResponseView,
  ReflectionPrompt as BackendReflectionPrompt,
} from '@/models/preformanceManagement';
import type { ReflectionData, ReflectionResponses } from './types';
import { resolveBackendPromptId, resolveUiPromptId } from './promptResolution';
import { formatLocalDateParam, apiDateOnlyParam, parseApiUtc } from '@/utils/apiDate';

const MonthlyReflectionsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { toast, showToast, dismissToast } = useToast();

  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [contributionStatus, setContributionStatus] = useState<ReflectionContributionStatusView | null>(null);
  const [userReflections, setUserReflections] = useState<UserReflectionView[]>([]);
  const [managerResponses, setManagerResponses] = useState<ManagerReflectionResponseView[]>([]);
  const [canSubmit, setCanSubmit] = useState<boolean>(true);
  // CO-591: the server's prompt rows for this tenant; every promptId posted is resolved off this.
  const [backendPrompts, setBackendPrompts] = useState<BackendReflectionPrompt[]>([]);

  const [reflectionData, setReflectionData] = useState<ReflectionData>({
    currentMonth: appMonthNameYear(new Date()),
    deadline: formatLocalDateParam(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)),
    prompts: [
      {
        id: 'contribution',
        title: t('performance.reflections.prompts.contribution.title'),
        category: t('performance.reflections.prompts.contribution.category'),
        icon: 'Trophy',
        question: t('performance.reflections.prompts.contribution.question'),
        subtext: t('performance.reflections.prompts.contribution.subtext'),
        placeholder: t('performance.reflections.prompts.contribution.placeholder'),
        tips: [
          t('performance.reflections.prompts.contribution.tips.0'),
          t('performance.reflections.prompts.contribution.tips.1'),
          t('performance.reflections.prompts.contribution.tips.2'),
        ],
      },
      {
        id: 'support',
        title: t('performance.reflections.prompts.support.title'),
        category: t('performance.reflections.prompts.support.category'),
        icon: 'Heart',
        question: t('performance.reflections.prompts.support.question'),
        subtext: t('performance.reflections.prompts.support.subtext'),
        placeholder: t('performance.reflections.prompts.support.placeholder'),
        tips: [
          t('performance.reflections.prompts.support.tips.0'),
          t('performance.reflections.prompts.support.tips.1'),
        ],
      },
      {
        id: 'growth',
        title: t('performance.reflections.prompts.growth.title'),
        category: t('performance.reflections.prompts.growth.category'),
        icon: 'TrendingUp',
        question: t('performance.reflections.prompts.growth.question'),
        subtext: t('performance.reflections.prompts.growth.subtext'),
        placeholder: t('performance.reflections.prompts.growth.placeholder'),
        tips: [
          t('performance.reflections.prompts.growth.tips.0'),
          t('performance.reflections.prompts.growth.tips.1'),
        ],
      },
      {
        id: 'challenges',
        title: t('performance.reflections.prompts.challenges.title'),
        category: t('performance.reflections.prompts.challenges.category'),
        icon: 'Shield',
        question: t('performance.reflections.prompts.challenges.question'),
        subtext: t('performance.reflections.prompts.challenges.subtext'),
        placeholder: t('performance.reflections.prompts.challenges.placeholder'),
        tips: [
          t('performance.reflections.prompts.challenges.tips.0'),
          t('performance.reflections.prompts.challenges.tips.1'),
        ],
      },
    ],
    responses: {},
    submittedPrompts: [],
  });

  // ====== PROMPT ID MAPPING ======
  // CO-591: resolved off the loaded prompt list (see ./promptResolution.ts),
  // never from a constant. Throws the message the card renders inline when nothing resolves.
  const requireBackendPromptId = (uiPromptId: string): number => {
    const id = resolveBackendPromptId(uiPromptId, backendPrompts);
    if (id === undefined) {
      throw new Error(t('performance.reflections.promptNotConfigured'));
    }
    return id;
  };

  // ====== DATE HELPERS ======
  const formatDate = (date: Date): string => {
    return appMonthNameYear(new Date(date)) || appMonthNameYear(new Date());
  };

  const formatDeadline = (date: Date): string => {
    const day = apiDateOnlyParam(date);
    if (day) return day;
    const now = new Date();
    return formatLocalDateParam(new Date(now.getFullYear(), now.getMonth() + 1, 0));
  };

  const getCurrentMonthDeadline = (): Date => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 25);
  };

  const getMonthString = (date: Date): string =>
    appMonthNameYear(new Date(date));

  const getMonthKey = (date: Date): string => {
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  };

  const areSameMonth = (date1: Date, date2: Date): boolean => {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth();
  };

  const getCurrentMonthDate = (): Date => {
    try {
      const parts = reflectionData.currentMonth.split(' ');
      if (parts.length === 2) {
        const month = new Date(`${parts[0]} 1, ${parts[1]}`).getMonth();
        const year = parseInt(parts[1]);
        return new Date(year, month, 1);
      }
    } catch {
      const now = new Date();
      return new Date(now.getFullYear(), now.getMonth(), 1);
    }
    return new Date();
  };

  // ====== DATA LOADING ======
  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const statusResult = await ReflectionAPI.getReflectionContributionStatus();

      if (statusResult) {
        setContributionStatus(statusResult);
        setCanSubmit(!statusResult.isDeadlinePassed);

        if (statusResult.submissionDeadline) {
          setReflectionData((prev) => ({
            ...prev,
            deadline: formatDeadline(statusResult.submissionDeadline),
            currentMonth: formatDate(statusResult.submissionDeadline),
          }));
        }
      } else {
        const deadline = getCurrentMonthDeadline();
        setReflectionData((prev) => ({
          ...prev,
          deadline: formatDeadline(deadline),
          currentMonth: formatDate(deadline),
        }));
        const isDeadlinePassed = new Date() > deadline;
        setCanSubmit(!isDeadlinePassed);
        if (isDeadlinePassed) {
          // Deadline info handled by deadlinePassed state — no error needed
        }
      }

      // CO-591: the prompt list must be in hand before the reflections are mapped onto cards —
      // a reflection's numeric promptId means nothing without it. A failed prompts fetch is not
      // fatal for reading (the page still shows history); it only makes every submit refuse with
      // the "not configured" message until a reload succeeds.
      let prompts: BackendReflectionPrompt[] = [];
      try {
        const promptsResult = await ReflectionAPI.getReflectionPrompts();
        prompts = Array.isArray(promptsResult) ? promptsResult : [];
      } catch {
        prompts = [];
      }
      setBackendPrompts(prompts);

      const reflectionsResult = await ReflectionAPI.getMyReflections();

      if (reflectionsResult && Array.isArray(reflectionsResult)) {
        setUserReflections(reflectionsResult);

        const responsesMap: ReflectionResponses = {};
        const submittedPrompts: string[] = [];

        reflectionsResult.forEach((reflection) => {
          const uiPromptId = resolveUiPromptId(reflection.promptId, prompts);
          // A reflection against a prompt this tenant no longer exposes has no card to land on;
          // defaulting it onto 'contribution' (the old behaviour) mis-marked that card as submitted.
          if (!uiPromptId) return;
          responsesMap[uiPromptId] = reflection.responseText;
          if (reflection.responseStatus === 'submitted') {
            submittedPrompts.push(uiPromptId);
          }
        });

        setReflectionData((prev) => ({
          ...prev,
          responses: { ...prev.responses, ...responsesMap },
          submittedPrompts,
        }));
      }

      try {
        const managerResponsesResult = await ReflectionAPI.getMyManagerResponses();

        if (managerResponsesResult && Array.isArray(managerResponsesResult)) {
          setManagerResponses(managerResponsesResult);

          const currentMonthDate = getCurrentMonthDate();
          const hasCurrentMonthResponse = managerResponsesResult.some((response) => {
            const responseDate = (parseApiUtc(response.createdOn) ?? new Date(NaN));
            return areSameMonth(responseDate, currentMonthDate);
          });

          if (hasCurrentMonthResponse) {
            setCanSubmit(false);
          }
        }
      } catch {
        // Manager responses are not critical
      }
    } catch {
      setError(t('performance.reflections.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ====== HANDLERS ======
  // CO-591 contract: both handlers RESOLVE only when the server accepted the write and REJECT
  // otherwise, with an Error whose message is the server's own refusal text (or the translated
  // fallback). ReflectionPromptCard catches the rejection, stays in edit mode and renders the
  // message inline — a swallowed failure here is exactly what made a 400 look like a saved card.
  const failureOf = (err: unknown, fallbackKey: string): Error => {
    const message = (err as { message?: unknown } | null | undefined)?.message;
    return new Error(typeof message === 'string' && message.trim() ? message : t(fallbackKey));
  };

  const handleSaveResponse = async (uiPromptId: string, response: string): Promise<void> => {
    try {
      setSaving(true);
      setError(null);

      const backendPromptId = requireBackendPromptId(uiPromptId);
      const input: MonthlyReflectionInput = { responseText: response, responseStatus: 'draft' };

      const result = await ReflectionAPI.saveOrSubmitReflection(backendPromptId, input);
      if (!result) throw new Error(t('performance.reflections.saveError'));

      setReflectionData((prev) => ({
        ...prev,
        responses: { ...prev.responses, [uiPromptId]: response },
      }));
      showToast(t('performance.reflections.saveSuccess'), 'success');
    } catch (err: unknown) {
      throw failureOf(err, 'performance.reflections.saveError');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitResponse = async (uiPromptId: string, response: string): Promise<void> => {
    if (!response?.trim()) {
      throw new Error(t('performance.reflections.emptyResponseError', 'Please write a response before submitting'));
    }

    try {
      setSaving(true);
      setError(null);

      const backendPromptId = requireBackendPromptId(uiPromptId);
      const input: MonthlyReflectionInput = { responseText: response, responseStatus: 'submitted' };

      try {
        await ReflectionAPI.saveOrSubmitReflection(backendPromptId, input);
      } catch (apiError: unknown) {
        // The one refusal that IS a saved state: the server already holds this month's submission.
        const message = (apiError as { message?: unknown } | null | undefined)?.message;
        if (typeof message === 'string' && message.includes('already submitted')) {
          setReflectionData((prev) => ({
            ...prev,
            responses: { ...prev.responses, [uiPromptId]: response },
            submittedPrompts: prev.submittedPrompts.includes(uiPromptId)
              ? prev.submittedPrompts
              : [...prev.submittedPrompts, uiPromptId],
          }));
          showToast(message, 'error');
          return;
        }
        throw apiError;
      }

      setReflectionData((prev) => ({
        ...prev,
        responses: { ...prev.responses, [uiPromptId]: response },
        submittedPrompts: prev.submittedPrompts.includes(uiPromptId)
          ? prev.submittedPrompts
          : [...prev.submittedPrompts, uiPromptId],
      }));

      showToast(t('performance.reflections.submitSuccess'), 'success');
      await loadData();
    } catch (err: unknown) {
      throw failureOf(err, 'performance.reflections.submitError');
    } finally {
      setSaving(false);
    }
  };

  // ====== COMPUTED ======
  const getCurrentMonthManagerResponse = () => {
    if (managerResponses.length === 0) return null;

    const currentMonthDate = getCurrentMonthDate();
    const currentMonthResps = managerResponses.filter((response) => {
      const responseDate = (parseApiUtc(response.createdOn) ?? new Date(NaN));
      return areSameMonth(responseDate, currentMonthDate);
    });

    if (currentMonthResps.length === 0) return null;

    const latest = currentMonthResps.sort(
      (a, b) => new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime()
    )[0];

    return {
      id: latest.id.toString(),
      message: latest.message || '',
      managerName: latest.managerName || t('common.manager', 'Manager'),
      managerAvatar: latest.managerImageUrl || '',
      createdAt: latest.createdOn,
      isRead: latest.isRead || false,
      encouragementType: latest.encouragementType || '',
      actionItems: latest.actionItems?.map((item) => item.itemText) || [],
      resources: latest.resources?.map((res) => ({ title: res.title, url: res.resourceUrl })) || [],
    };
  };

  const getPreviousReflectionsData = () => {
    if (userReflections.length === 0) return [];

    const currentMonthDate = getCurrentMonthDate();

    const groupedByMonth = userReflections.reduce(
      (groups, reflection) => {
        const reflectionDate = reflection.submittedAt
          ? (parseApiUtc(reflection.submittedAt) ?? new Date(NaN))
          : (parseApiUtc(reflection.createdOn) ?? new Date(NaN));

        if (isNaN(reflectionDate.getTime())) return groups;

        const monthKey = getMonthKey(reflectionDate);
        const monthString = getMonthString(reflectionDate);

        if (areSameMonth(reflectionDate, currentMonthDate)) return groups;

        if (!groups[monthKey]) {
          groups[monthKey] = { reflections: [], monthString, date: reflectionDate };
        }
        groups[monthKey].reflections.push(reflection);
        return groups;
      },
      {} as Record<string, { reflections: UserReflectionView[]; monthString: string; date: Date }>
    );

    return Object.entries(groupedByMonth)
      .map(([monthKey, data]) => {
        const submitted = data.reflections.filter((r) => r.responseStatus === 'submitted');
        const monthManagerResponse = managerResponses.find((response) => {
          const responseDate = (parseApiUtc(response.createdOn) ?? new Date(NaN));
          return areSameMonth(responseDate, data.date);
        });

        return {
          id: monthKey,
          month: data.date.toISOString(),
          monthString: data.monthString,
          status: submitted.length === data.reflections.length ? 'complete' : 'partial',
          completedPrompts: submitted.length,
          totalPrompts: data.reflections.length,
          responses: data.reflections.map((r) => ({
            prompt: r.promptTitle || t('common.reflection', 'Reflection'),
            answer: r.responseText || '',
          })),
          managerResponse: monthManagerResponse?.message || '',
        };
      })
      .sort((a, b) => new Date(b.month).getTime() - new Date(a.month).getTime());
  };

  const completedCount = reflectionData.submittedPrompts.length;
  const totalCount = reflectionData.prompts.length;
  const isAllComplete = completedCount === totalCount;
  const deadlinePassed = contributionStatus?.isDeadlinePassed || false;
  const currentMonthManagerResponse = getCurrentMonthManagerResponse();
  const previousReflectionsData = getPreviousReflectionsData();
  const hasManagerResponse = currentMonthManagerResponse !== null;
  const daysUntilDeadline = Math.ceil(
    (new Date(reflectionData.deadline).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
  );

  // ====== LOADING STATE ======
  if (loading) {
    return (
      <PageLayout
        title={t('performance.reflections.pageTitle', { month: reflectionData.currentMonth })}
        description={t('performance.reflections.pageSubtitle')}
        icon={BookOpen}
        accentColor="rose"
        maxWidth="1600px"
      >
        <div className="text-center py-16">
          <Spinner size="lg" />
          <p className="text-lg text-muted-foreground mt-6">
            {t('performance.reflections.loadingData')}
          </p>
        </div>
      </PageLayout>
    );
  }

  // ====== ERROR STATE ======
  if (error && !loading) {
    return (
      <PageLayout
        title={t('performance.reflections.pageTitle', { month: reflectionData.currentMonth })}
        description={t('performance.reflections.pageSubtitle')}
        icon={BookOpen}
        accentColor="rose"
        maxWidth="1600px"
      >
        <Alert variant="error" className="mb-6">{error}</Alert>

        <div className="text-center py-16">
          <AppIcon name="AlertCircle" size={60} className="text-destructive mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-foreground mb-4">
            {t('performance.reflections.unableToLoad')}
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            {t('performance.reflections.checkConnection')}
          </p>
          <Button onClick={() => window.location.reload()}>
            {t('performance.reflections.retry')}
          </Button>
        </div>
      </PageLayout>
    );
  }

  // ====== MAIN RENDER ======
  return (
    <PageLayout
      title={t('performance.reflections.pageTitle', { month: reflectionData.currentMonth })}
      description={t('performance.reflections.pageSubtitle')}
      icon={BookOpen}
      accentColor="rose"
      maxWidth="1600px"
      headerActions={
        <StatusBadge variant={daysUntilDeadline <= 7 ? 'error' : 'info'} icon={Bell}>
          {t('performance.reflections.dueIn', { days: daysUntilDeadline })}
        </StatusBadge>
      }
    >
      <ToastRenderer toast={toast} onDismiss={dismissToast} />

      {deadlinePassed && <DeadlineWarning month={reflectionData.currentMonth} />}

      {!deadlinePassed && currentMonthManagerResponse && (
        <Alert variant="info" className="mb-6">
          {t('performance.reflections.managerReviewedMessage', {
            month: reflectionData.currentMonth,
          })}
        </Alert>
      )}

      {/* Why Reflect Card */}
      <div className="border border-border rounded-xl p-6 mb-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-lg bg-gradient-to-r from-rose-500 to-pink-600 flex items-center justify-center shrink-0">
            <AppIcon name="Lightbulb" size={28} className="text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-1">
              {t('performance.reflections.whyReflect')}
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t('performance.reflections.whyReflectDescription')}
            </p>
          </div>
        </div>
      </div>

        {/* Mobile: Tabs layout */}
        <div className="lg:hidden">
          <Tabs defaultValue="current" className="w-full">
            <TabsList className="w-full mb-6">
              <TabsTrigger value="current" className="flex-1 gap-2">
                <AppIcon name="Pencil" size={16} />
                {t('performance.reflections.tabCurrent')}
              </TabsTrigger>
              <TabsTrigger value="manager" className="flex-1 gap-2">
                <AppIcon name="MessageSquare" size={16} />
                {t('performance.reflections.tabManager')}
              </TabsTrigger>
              <TabsTrigger value="previous" className="flex-1 gap-2">
                <AppIcon name="History" size={16} />
                {t('performance.reflections.tabPrevious')}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="current">
              <ReflectionProgress
                totalPrompts={totalCount}
                completedPrompts={completedCount}
                currentMonth={reflectionData.currentMonth}
              />
              <div className="mt-6">
                {reflectionData.prompts.map((prompt, index) => {
                  const isSubmitted = reflectionData.submittedPrompts.includes(prompt.id);
                  const canSave = !deadlinePassed && !hasManagerResponse;
                  const canSubmitThisPrompt = canSubmit && !isSubmitted && !hasManagerResponse;

                  return (
                    <ReflectionPromptCard
                      key={prompt.id}
                      prompt={prompt}
                      response={reflectionData.responses[prompt.id] || ''}
                      isSubmitted={isSubmitted}
                      onSave={handleSaveResponse}
                      onSubmit={handleSubmitResponse}
                      index={index}
                      totalPrompts={totalCount}
                      canSave={canSave}
                      canSubmit={canSubmitThisPrompt}
                      deadlinePassed={deadlinePassed}
                    />
                  );
                })}
              </div>
            </TabsContent>

            <TabsContent value="manager">
              <ManagerResponseCard response={currentMonthManagerResponse} />
            </TabsContent>

            <TabsContent value="previous">
              <PreviousReflections reflections={previousReflectionsData} />
            </TabsContent>
          </Tabs>
        </div>

        {/* Desktop: Grid layout */}
        <div className="hidden lg:block">
          <ReflectionContent
            reflectionData={reflectionData}
            currentMonthManagerResponse={currentMonthManagerResponse}
            previousReflectionsData={previousReflectionsData}
            completedCount={completedCount}
            totalCount={totalCount}
            isAllComplete={isAllComplete}
            canSubmit={canSubmit}
            deadlinePassed={deadlinePassed}
            onSave={handleSaveResponse}
            onSubmit={handleSubmitResponse}
          />
        </div>

      {saving && <LoadingOverlay message={t('performance.reflections.savingResponse')} />}
    </PageLayout>
  );
};

export default MonthlyReflectionsPage;
